/* eslint-disable no-console */
/* eslint-disable no-restricted-syntax */
import { mapKeys, mapValues, pickBy, startsWith } from 'lodash';

const STORAGE_KEY = 'mf_remotes_config';
const CONFIG_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const FETCH_TIMEOUT_MS = 4000;

/**
 * Build config path based on podName
 * Special case: 'demo' uses production.json
 */
function getConfigPath(podName) {
  // demo uses the same config as production
  if (podName === 'demo') {
    return 'production.json';
  }

  // For canada-production and uk-production, use production.json for now
  // (unless we create specific JSONs for them in mf-versions)
  if (podName === 'canada-production' || podName === 'uk-production') {
    return 'production.json';
  }

  return `${podName}.json`;
}

/**
 * Get cached config from localStorage
 * Returns config with staleness indicator
 */
function getCachedConfig(podName) {
  try {
    const cached = localStorage.getItem(`${STORAGE_KEY}_${podName}`);
    if (!cached) return null;

    const { config, timestamp, podName: cachedPod } = JSON.parse(cached);

    // Validate it's for the same pod
    if (cachedPod !== podName) return null;

    const age = Date.now() - timestamp;
    return {
      config,
      timestamp,
      age,
      isStale: age > CONFIG_TTL_MS,
    };
  } catch {
    return null;
  }
}

/**
 * Save config to localStorage with timestamp
 */
function setCachedConfig(podName, config) {
  try {
    localStorage.setItem(
      `${STORAGE_KEY}_${podName}`,
      JSON.stringify({
        config,
        timestamp: Date.now(),
        podName,
      }),
    );
  } catch (e) {
    console.warn('Failed to cache remotes config:', e);
  }
}

/**
 * Build remotes URLs from config
 */
function buildRemotesFromConfig(base, versions) {
  const remotes = {};

  for (const [remote, version] of Object.entries(versions)) {
    const baseParsed = base.replace('{name}', remote).replace('{version}', version || '');
    let url = `${baseParsed}/remoteEntry.js`;

    // Avoid caching if the url does not contain the version
    if (!/\/\d+\.\d+\.\d+\//.test(url)) {
      url += `?r=${Math.random()}`;
    }
    remotes[remote] = url;
  }

  return remotes;
}

/**
 * Build remotes URLs from build-time environment variables (fallback)
 * This is the original getRemoteUrls logic from web-app
 */
function getRemoteUrlsFromEnv() {
  let remotes = pickBy(process.env, (_, key) => startsWith(key, 'REACT_APP_VERSION_REMOTE'));
  remotes = mapKeys(remotes, (_, key) => key.replace('REACT_APP_VERSION_', '').toLowerCase());
  remotes = mapValues(remotes, (version, key) => {
    // obtain the base url from the environment variable or the version string
    const base = /^http/.test(version || '') ? version : process.env.REACT_APP_REMOTES_BASE;
    const baseParsed = base.replace('{name}', key).replace('{version}', version || '');
    let url = `${baseParsed}/remoteEntry.js`;

    // avoid caching if the url does not contain the version
    if (!/\/\d+\.\d+\.\d+\//.test(url)) url += `?r=${Math.random()}`;
    return url;
  });
  return remotes;
}

/**
 * Helper to return cached config or null
 */
function getFallbackFromCache(cached, podName) {
  if (cached?.config?.versions) {
    const ageMinutes = Math.round(cached.age / 1000 / 60);
    console.log(`Using cached config for ${podName} (age: ${ageMinutes}min${cached.isStale ? ', stale' : ''})`);
    return { versions: cached.config.versions, source: 'cache' };
  }
  return null;
}

/**
 * Load remotes config from CDN at runtime with localStorage fallback
 * Priority: CDN -> localStorage cache -> null (triggers build-time fallback)
 * Returns { versions, source } if found, null otherwise
 */
async function loadRemotesConfig(podName) {
  // base is required to build URLs - without it, fall back to build-time env vars
  if (!window.hostConfig?.remotes?.base) {
    console.warn('Missing hostConfig.remotes.base, cannot use runtime config');
    return null;
  }

  const siteNext = 'ui-next.lumaplatform.com';
  const configPath = getConfigPath(podName);
  const configUrl = `https://${siteNext}/config/${configPath}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  const cached = getCachedConfig(podName);

  try {
    const response = await fetch(configUrl, {
      cache: 'no-cache',
      headers: {
        Accept: 'application/json',
      },
      signal: controller.signal,
    });

    if (response.ok) {
      const remoteConfig = await response.json();

      // Validate structure
      if (!remoteConfig.versions) {
        console.warn('Invalid remotes config structure: missing versions');
        return getFallbackFromCache(cached, podName);
      }

      // Success - update cache and return
      setCachedConfig(podName, remoteConfig);
      return { versions: remoteConfig.versions, source: 'cdn' };
    }

    // 404 = no runtime config available for this pod
    if (response.status === 404) {
      console.log(`No runtime config found for ${podName} (${configPath})`);
      return null;
    }

    console.warn(`Failed to fetch remotes config: ${response.status} ${response.statusText}`);
    return getFallbackFromCache(cached, podName);
  } catch (error) {
    if (error?.name === 'AbortError') {
      console.warn(`Remotes config fetch timed out (${FETCH_TIMEOUT_MS}ms): ${configUrl}`);
    } else {
      console.warn('Failed to load remotes config from CDN:', error);
    }
    return getFallbackFromCache(cached, podName);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Extract build-time remotes that point to localhost or Android emulator,
 * so they are preserved even when CDN runtime config is loaded.
 */
function getLocalOverrides(remotes) {
  const overrides = {};
  for (const [key, url] of Object.entries(remotes)) {
    if (url.includes('localhost') || url.includes('10.0.2.2')) {
      overrides[key] = url;
    }
  }
  return overrides;
}

/**
 * Initialize runtime config for module federation remotes
 * Priority: CDN -> localStorage cache -> build-time env vars
 */
async function startRuntimeConfig() {
  const podName = window?.hostConfig?.podName;
  const buildTimeRemotes = getRemoteUrlsFromEnv();

  if (!podName) {
    // @ts-expect-error
    window.UINEXT_REMOTES = buildTimeRemotes;
    console.warn('No podName configured, using build-time env vars');
    return;
  }

  const remotesConfig = await loadRemotesConfig(podName);

  if (remotesConfig) {
    const runtimeRemotes = buildRemotesFromConfig(window.hostConfig.remotes?.base, remotesConfig.versions);

    const localOverrides = getLocalOverrides(buildTimeRemotes);
    // @ts-expect-error
    window.UINEXT_REMOTES = { ...runtimeRemotes, ...localOverrides };

    if (Object.keys(localOverrides).length > 0) {
      console.log(`Local dev overrides: ${Object.keys(localOverrides).join(', ')}`);
    }

    const sourceLabel = remotesConfig.source === 'cdn' ? 'CDN' : 'cache';
    console.log(`Loaded runtime config for ${podName} (source: ${sourceLabel})`);
  } else {
    // @ts-expect-error
    window.UINEXT_REMOTES = buildTimeRemotes;
    console.log(`Using build-time env vars for ${podName}`);
  }
}

export { startRuntimeConfig };
